import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Card,
  Table,
  Tag,
  Image,
  Row,
  Col,
  Statistic,
  Typography,
  Select,
  Button,
  DatePicker,
  Progress,
} from "antd";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import { LineWave } from "react-loader-spinner";
import {
  DollarCircleOutlined,
  FilePdfOutlined,
  BarChartOutlined,
  SafetyCertificateOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { Bar, Pie } from "react-chartjs-2";
//import { useReactToPrint } from "react-to-print";
import "./Styles.css";
import moment from "moment";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const CollateralReport = () => {
  const { company } = useContext(AppContext);
  const [collateral, setCollateral] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([
    moment().subtract(1, "year"),
    moment(),
  ]);
  const [statusFilter, setStatusFilter] = useState("all");
  const componentRef = useRef();
  const token = localStorage.getItem("token");
  const { Option } = Select;

  // Enhanced status mapping
  const statusColorMap = {
    "Pending Verification": { color: "orange", risk: "High" },
    Active: { color: "green", risk: "Low" },
    Sold: { color: "red", risk: "Closed" },
    Released: { color: "blue", risk: "N/A" },
  };

  // Dummy data template for missing fields
  const dummyDataTemplate = {
    collateral_type: "General Assets",
    condition: "Good",
    stored_location: "Main Warehouse",
    is_insured: true,
    insurance_details: {
      provider: "ABC Insurance",
      coverage_amount: 15000,
      expiry_date: "2025-12-31",
    },
  };

  useEffect(() => {
    const fetchCollateral = async () => {
      try {
        const response = await axios.get(
          `https://zedlenders.pickmesms.com/items/collateral_register?filter[company]=${company.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const enhancedData = response.data.data.map((item) => ({
          ...dummyDataTemplate,
          ...item,
          documents: JSON.parse(item.documents),
          registrationDate:
            item.registrationDate || new Date().toISOString().split("T")[0],
          collateral_type: item.collateral_type || "Uncategorized",
          insurance_coverage: item.is_insured
            ? item.insurance_details?.coverage_amount ||
              item.valuation_amount * 0.8
            : 0,
        }));

        setCollateral(enhancedData);
        setFilteredData(enhancedData);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching collateral:", err);
        setLoading(false);
      }
    };

    fetchCollateral();
  }, [token, company.id]);

  //   const handlePrint = useReactToPrint({
  //     // content: () => componentRef.current,
  //     // documentTitle: "Collateral-Asset-Report",
  //   });

  const calculateMetrics = () => {
    const totalValue = filteredData.reduce(
      (sum, item) => sum + item.valuation_amount,
      0
    );
    const insuredValue = filteredData.reduce(
      (sum, item) => sum + (item.is_insured ? item.valuation_amount : 0),
      0
    );
    const riskExposure = filteredData.reduce(
      (sum, item) =>
        sum +
        (item.status === "Active"
          ? item.valuation_amount - item.insurance_coverage
          : 0),
      0
    );

    return {
      totalValue,
      insuredValue,
      riskExposure,
      coverageRatio: ((insuredValue / totalValue) * 100).toFixed(1),
      activeCollateral: filteredData.filter((item) => item.status === "Active")
        .length,
    };
  };

  const statusDistributionChart = {
    labels: Object.keys(statusColorMap),
    datasets: [
      {
        data: Object.keys(statusColorMap).map(
          (status) =>
            filteredData.filter((item) => item.status === status).length
        ),
        backgroundColor: Object.values(statusColorMap).map((s) => s.color),
      },
    ],
  };

  const valueByTypeChart = {
    labels: [...new Set(filteredData.map((item) => item.collateral_type))],
    datasets: [
      {
        label: "Collateral Value by Type",
        data: [
          ...new Set(filteredData.map((item) => item.collateral_type)),
        ].map((type) =>
          filteredData
            .filter((item) => item.collateral_type === type)
            .reduce((sum, item) => sum + item.valuation_amount, 0)
        ),
        backgroundColor: ["#1890ff", "#52c41a", "#722ed1", "#ff4d4f"],
      },
    ],
  };

  const reportColumns = [
    {
      title: "Asset Details",
      children: [
        {
          title: "Description",
          dataIndex: "description",
          render: (text, record) => (
            <div>
              <Text strong>{text}</Text>
              <div>
                <Text type="secondary">Type: {record.collateral_type}</Text>
              </div>
            </div>
          ),
        },
        {
          title: "Value (ZMW)",
          dataIndex: "valuation_amount",
          render: (val) => <Text strong>{val?.toLocaleString() || "N/A"}</Text>,
          sorter: (a, b) => a.valuation_amount - b.valuation_amount,
        },
      ],
    },
    {
      title: "Risk Profile",
      children: [
        {
          title: "Status",
          dataIndex: "status",
          render: (status) => (
            <div>
              <Tag color={statusColorMap[status].color}>{status}</Tag>
              <div>
                <Text type="secondary">
                  Risk: {statusColorMap[status].risk}
                </Text>
              </div>
            </div>
          ),
        },
        {
          title: "Insurance Coverage",
          render: (_, record) => (
            <div>
              <Text>{record.is_insured ? "Insured" : "Uninsured"}</Text>
              {record.is_insured && (
                <div>
                  <Text type="secondary">
                    {record.insurance_details.provider} (ZMW{" "}
                    {record.insurance_details.coverage_amount?.toLocaleString()}
                    )
                  </Text>
                </div>
              )}
            </div>
          ),
        },
      ],
    },
    {
      title: "Collateral Utilization",
      children: [
        {
          title: "Associated Loan",
          dataIndex: "loan",
          render: (loan) =>
            loan ? <Tag color="blue">{loan}</Tag> : "Unpledged",
        },
        {
          title: "Loan-to-Value",
          render: (_, record) => (
            <Progress
              percent={(
                (record.loan?.amount / record.valuation_amount) *
                100
              ).toFixed(1)}
              status={record.loan ? "active" : "normal"}
              format={(percent) => (record.loan ? `${percent}%` : "N/A")}
            />
          ),
        },
      ],
    },
  ];

  if (loading) {
    return (
      <div className="center-loader">
        <LineWave visible={true} height="100" width="100" />
      </div>
    );
  }

  return (
    <div ref={componentRef} className="collateral-report">
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ marginBottom: 0 }}>
            <SafetyCertificateOutlined /> Collateral Asset Report
          </Title>
          <Text type="secondary">
            {company.name} • As of {moment().format("MMM D, YYYY")}
          </Text>
        </Col>
        <Col>
          <Button icon={<FilePdfOutlined />} onClick={""}>
            Export PDF
          </Button>
        </Col>
      </Row>

      {/* Report Filters */}
      <Card bordered={false} style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={8}>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
              style={{ width: "100%" }}
            />
          </Col>
          <Col span={8}>
            <Select
              value={statusFilter}
              onChange={(value) => setStatusFilter(value)}
              style={{ width: "100%" }}
            >
              <Option value="all">All Statuses</Option>
              {Object.keys(statusColorMap).map((status) => (
                <Option key={status} value={status}>
                  {status}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Button block icon={<BarChartOutlined />}>
              Generate Comparative Analysis
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Key Metrics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Collateral Value"
              value={calculateMetrics().totalValue}
              prefix="ZMW"
              valueStyle={{ color: "#3f8600" }}
              formatter={(value) => value.toLocaleString()}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Insured Value"
              value={calculateMetrics().insuredValue}
              prefix="ZMW"
              valueStyle={{ color: "#1890ff" }}
              formatter={(value) => value.toLocaleString()}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Risk Exposure"
              value={calculateMetrics().riskExposure}
              prefix="ZMW"
              valueStyle={{ color: "#cf1322" }}
              formatter={(value) => value.toLocaleString()}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Insurance Coverage"
              value={calculateMetrics().coverageRatio}
              suffix="%"
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Visual Analytics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card title="Collateral Status Distribution">
            <Pie
              data={statusDistributionChart}
              options={{
                plugins: {
                  legend: { position: "bottom" },
                  tooltip: {
                    callbacks: {
                      label: (ctx) =>
                        `${ctx.label}: ${ctx.raw} assets (ZMW ${filteredData
                          .filter((item) => item.status === ctx.label)
                          .reduce((sum, item) => sum + item.valuation_amount, 0)
                          .toLocaleString()})`,
                    },
                  },
                },
              }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Collateral Value by Asset Type">
            <Bar
              data={valueByTypeChart}
              options={{
                responsive: true,
                scales: {
                  y: {
                    beginAtZero: true,
                    title: { display: true, text: "Value (ZMW)" },
                    ticks: {
                      callback: (value) => `ZMW ${value.toLocaleString()}`,
                    },
                  },
                },
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Detailed Report */}
      <Card
        title="Collateral Asset Register"
        bordered={false}
        extra={`${filteredData.length} Assets Registered`}
      >
        <Table
          columns={reportColumns}
          dataSource={filteredData}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          expandable={{
            expandedRowRender: (record) => (
              <div className="asset-details-expanded">
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong>Storage Details:</Text>
                    <p>Location: {record.stored_location}</p>
                    <p>Condition: {record.condition}</p>

                    <Text strong>Insurance Information:</Text>
                    {record.is_insured ? (
                      <div>
                        <p>Provider: {record.insurance_details.provider}</p>
                        <p>
                          Coverage: ZMW{" "}
                          {record.insurance_details.coverage_amount?.toLocaleString()}
                        </p>
                        <p>
                          Expiry:{" "}
                          {moment(record.insurance_details.expiry_date).format(
                            "MMM D, YYYY"
                          )}
                        </p>
                      </div>
                    ) : (
                      <Text type="warning">No insurance coverage</Text>
                    )}
                  </Col>
                  <Col span={12}>
                    <Text strong>Documentation:</Text>
                    <Image.PreviewGroup>
                      <Row gutter={[8, 8]}>
                        {record.documents?.map((img, index) => (
                          <Col span={8} key={index}>
                            <Image src={img} style={{ borderRadius: 4 }} />
                          </Col>
                        ))}
                      </Row>
                    </Image.PreviewGroup>
                  </Col>
                </Row>
              </div>
            ),
          }}
        />
      </Card>

      {/* Risk Assessment Section */}
      <Card
        title="Risk Management Summary"
        style={{ marginTop: 24 }}
        bordered={false}
      >
        <Row gutter={16}>
          <Col span={12}>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Collateral Coverage Ratio:</Text>
              <Progress
                percent={calculateMetrics().coverageRatio}
                status={
                  calculateMetrics().coverageRatio > 75
                    ? "success"
                    : "exception"
                }
                format={(percent) => `${percent}% Insured`}
              />
            </div>
            <div>
              <Text strong>Top Risk Exposures:</Text>
              <ul>
                {filteredData
                  .filter(
                    (item) =>
                      item.status === "Active" &&
                      item.valuation_amount > item.insurance_coverage
                  )
                  .sort(
                    (a, b) =>
                      b.valuation_amount -
                      b.insurance_coverage -
                      (a.valuation_amount - a.insurance_coverage)
                  )
                  .slice(0, 3)
                  .map((item) => (
                    <li key={item.id}>
                      {item.description} (Exposure: ZMW{" "}
                      {(
                        item.valuation_amount - item.insurance_coverage
                      ).toLocaleString()}
                      )
                    </li>
                  ))}
              </ul>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Collateral Liquidity Analysis:</Text>
              <ul>
                <li>
                  Immediately Liquid: ZMW{" "}
                  {filteredData
                    .filter((item) => item.collateral_type === "Liquid Assets")
                    .reduce((sum, item) => sum + item.valuation_amount, 0)
                    .toLocaleString()}
                </li>
                <li>
                  Requires Valuation: ZMW{" "}
                  {filteredData
                    .filter((item) => item.status === "Pending Verification")
                    .reduce((sum, item) => sum + item.valuation_amount, 0)
                    .toLocaleString()}
                </li>
              </ul>
            </div>
            <div>
              <Text strong>Recommendations:</Text>
              <ul>
                <li>Increase insurance coverage on high-value assets</li>
                <li>Accelerate verification of pending collateral</li>
                <li>Review storage conditions for long-term assets</li>
              </ul>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default CollateralReport;
